import '../../../styles/pages.css'

import React, { useState, useEffect } from 'react'
import { ClassAssignmentList } from './ClassAssignmentList'
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
} from '@mui/material'
import { getAPIRequest } from 'infrastructure/api'
import RequestDialog from '../../dialogs/RequestDialog'
import { Class, ClassUserInfo, KiddomClassInfo } from 'infrastructure/api'
import { useUrlSearchParam } from 'shared/hooks/useSearchParams'
import { AccordionWrapper } from '../../utility/AccordianWrapper'
import { Table } from 'shared/ui/Table/Table'
import { makeKiddomPatchRequest as patchAPIRequest } from 'infrastructure/api'

export function ClassShow() {
  const [classIDParam, setClassIDParam] = useUrlSearchParam('class_id') ?? ''
  const [classID, setClassID] = useState(classIDParam ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<KiddomClassInfo | null>(null)

  const fetchClass = async (id: string) => {
    setError(null)
    setLoading(true)
    try {
      const res: any = await getAPIRequest<KiddomClassInfo>(
        `/v1/kat/class/${id}`,
      )

      if (res) {
        setData(res)
      } else {
        setData(null)
        setError(`No class found with id: ${id}`)
      }
    } catch (e: any) {
      setError(e?.message || 'Error fetching class')
      setData(null)
    }
    setLoading(false)
  }

  const handleSearch = () => {
    if (classID.trim() === '') return
    // Update the query param
    setClassIDParam(classID.trim())
  }

  useEffect(() => {
    if (classIDParam && classIDParam.trim() !== '') {
      setClassID(classIDParam)
      fetchClass(classIDParam)
    }
  }, [classIDParam])

  return (
    <div className="content-color">
      <div className="section-buttons">
        <Typography variant="h6">Class Lookup</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            label="Class ID"
            value={classID}
            onChange={(e) => setClassID(e.target.value)}
            size="small"
          />
          <Button variant="outlined" onClick={handleSearch} disabled={loading}>
            {loading ? 'Loading...' : 'Fetch'}
          </Button>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
      </div>

      <div>
        {data ? (
          <Box>
            {data.class_info ? (
              <ClassDetails classInfo={data.class_info} />
            ) : null}

            <br />
            <AccordionWrapper title="Enrollments:">
              <ClassEnrollmentList classUserInfo={data.class_user_info} />
            </AccordionWrapper>

            <br />
            <AccordionWrapper title="Assignments:">
              <ClassAssignmentList classID={data.class_info?.class_id} />
            </AccordionWrapper>
          </Box>
        ) : (
          <p>Enter a Class ID and click Fetch to view details.</p>
        )}
      </div>
    </div>
  )
}

function ClassDetails({ classInfo }: { classInfo?: Class }) {
  if (!classInfo) return null

  return (
    <Box>
      <p>
        <b>Class Details</b>
      </p>

      <p>
        Class ID:<b> {classInfo.class_id} </b>
      </p>
      <p>
        Class Name:<b> {classInfo.class_name} </b>
      </p>
      <p>
        Class Alias:
        <b> {classInfo.class_alias ? classInfo.class_alias : 'N/A'} </b>
      </p>
      <p>
        Year:<b> {classInfo.year} </b>
      </p>
      <p>
        Is Archived:<b> {classInfo.is_archived ? 'Yes' : 'No'} </b>
      </p>
      <p>
        Class Code:
        <b>
          {' '}
          {classInfo.class_voucher_code
            ? classInfo.class_voucher_code
            : 'N/A'}{' '}
        </b>
      </p>
      <p>
        Calculation Method:
        <b>
          {' '}
          {classInfo.calculation_method
            ? classInfo.calculation_method
            : 'N/A'}{' '}
        </b>
      </p>
      <p>
        Is Demo:<b> {classInfo.is_demo ? 'Yes' : 'No'} </b>
      </p>
      <p>
        Is Staffroom:<b> {classInfo.is_staffroom ? 'Yes' : 'No'} </b>
      </p>
    </Box>
  )
}

export function ClassEnrollmentList({
  classUserInfo,
}: {
  classUserInfo: ClassUserInfo[]
}) {
  const [permissions, setPermissions] = useState<Record<number, string>>({})

  useEffect(() => {
    const initial: Record<number, string> = {}
    ;(classUserInfo || []).forEach((u) => {
      const label =
        u.role === 'coteacher'
          ? 'coteacher'
          : u.role === 'collaborator'
            ? 'collaborator'
            : ''
      initial[u.user_id] = permissions[u.user_id] ?? label
    })
    setPermissions(initial)
  }, [classUserInfo])

  const onPermissionSelected = (userId: number, event: any) => {
    setPermissions((prev) => ({
      ...prev,
      [userId]: event.target.value,
    }))
  }

  const columns = [
    {
      title: <strong>UserID</strong>,
      width: 80,
      rowContent: (c: ClassUserInfo) => c.user_id,
    },
    {
      title: <strong>ClassID</strong>,
      width: 80,
      rowContent: (c: ClassUserInfo) => c.class_id,
    },
    {
      title: <strong>Email</strong>,
      width: 150,
      rowContent: (c: ClassUserInfo) => c.email,
    },
    {
      title: <strong>Name</strong>,
      width: 150,
      rowContent: (c: ClassUserInfo) => c.name,
    },
    {
      title: <strong>Role</strong>,
      width: 150,
      rowContent: (c: ClassUserInfo) => c.role,
    },
    {
      title: <strong>Set Permissions</strong>,
      width: 280,
      rowContent: (c: ClassUserInfo) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select
            size="small"
            value={permissions[c.user_id] ?? ''}
            onChange={(event) => onPermissionSelected(c.user_id, event)}
            data-testid={`assign-role-${c.user_id}`}
          >
            <MenuItem value="coteacher">Can Edit</MenuItem>
            <MenuItem value="collaborator">Can View</MenuItem>
          </Select>
          {permissions[c.user_id] ? (
            <SetPermissionsButton
              classID={c.class_id}
              userID={c.user_id}
              permission={permissions[c.user_id]}
              originalRole={c.role}
              dataTestId={`set-permissions-${c.user_id}`}
            />
          ) : null}
        </Box>
      ),
    },
  ]
  return (
    <div>
      {classUserInfo && classUserInfo.length > 0 ? (
        <Table title={null} columns={columns} items={classUserInfo} />
      ) : (
        <p>No users found for this class.</p>
      )}
    </div>
  )
}

function SetPermissionsButton({
  classID,
  userID,
  permission,
  originalRole,
  dataTestId,
}: {
  classID: number
  userID: number
  permission: string
  originalRole: string
  dataTestId?: string
}) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  const title = `Are you sure you want to modify the permissions?`
  const content = [
    {
      key: `User ID`,
      value: userID.toString(),
    },
    {
      key: `New Permission`,
      value:
        permission === 'coteacher'
          ? 'Can Edit'
          : permission === 'collaborator'
            ? 'Can View'
            : 'N/A',
    },
    {
      key: `Existing Permissions`,
      value:
        originalRole === 'coteacher'
          ? 'Can Edit'
          : originalRole === 'collaborator'
            ? 'Can View'
            : 'N/A',
    },
  ]

  const handleChangePermissions = async (
    classID: number,
    userID: number,
    permission: string,
  ) => {
    try {
      const res = await patchAPIRequest({
        kiddomPath: `/v1/kat/class/${classID}/permissions`,
        requestBody: {
          role: permission,
          user_ids: [userID],
        },
      })

      if (res) {
        return
      }
      setError(`Error`)
    } catch (e: any) {
      setError(e?.message || 'Error setting permissions')
    }
  }

  return (
    <div className="section-buttons">
      <Button
        variant="outlined"
        color="secondary"
        disabled={originalRole === permission}
        onClick={() => setOpen(true)}
        data-testid={dataTestId}
      >
        Set Permissions
      </Button>
      {error && <p className="error">{error}</p>}

      <RequestDialog
        open={open}
        title={title}
        content={content}
        submitRequest={() =>
          handleChangePermissions(classID, userID, permission)
        }
        waitingTime={2000}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}
